import { Project } from '../../../core/objects/project';
import { Group } from '../../../core/types';
import { DragDropEvent } from '../loads-list.component';
import { ProjectService } from 'src/app/core/services/project.service';

export function generateUpdateDragAndDrop(
  event: DragDropEvent,
  type: 'from' | 'to',
  project: Project,
  getGroupsSorting: (workSpaceIndex: number, cargoSpaceIndex: number, stopIndex: number, isUnloadingSpace: boolean) => number[],
): any {
  const element = event[type];
  // Get dataset from element - Angular CDK may wrap elements, so ensure we get the actual dataset
  // Also check attributes directly as fallback
  const data: DOMStringMap = element?.dataset || {};
  
  // Helper to get attribute value (fallback if dataset doesn't work)
  // Maps camelCase property names to kebab-case attribute names
  const attrNameMap: Record<string, string> = {
    'workSpaceIndex': 'work-space-index',
    'cargoSpaceIndex': 'cargo-space-index',
    'stopIndex': 'stop-index',
    'isUnloadingSpace': 'is-unloading-space'
  };
  
  const getAttr = (name: string): string | undefined => {
    if (data[name] !== undefined) {
      return data[name];
    }
    // Try direct attribute access as fallback
    const attrName = attrNameMap[name] || name.replace(/([A-Z])/g, '-$1').toLowerCase();
    const fullAttrName = `data-${attrName}`;
    const attrValue = element?.getAttribute(fullAttrName);
    return attrValue !== null ? attrValue : undefined;
  };
  
  const workSpaceIndex: number = parseInt(data.workSpaceIndex || getAttr('workSpaceIndex') || '');
  const workSpaceId: number = project.workSpaces[workSpaceIndex].id;
  let cargoSpaceId: number;
  let stopId: number = undefined;
  
  // Validation: check if workspace exists and has multi-stops
  const workSpace = project.workSpaces[workSpaceIndex];
  const hasMultiStops: boolean = workSpace?.stops?.length > 0;
  
  // Check if the specific element has the data-stop-index attribute (is a multistop)
  // instead of checking if the entire workspace has multistops
  const stopIndexAttr = data.stopIndex !== undefined ? data.stopIndex : getAttr('stopIndex');
  
  if (stopIndexAttr !== undefined && stopIndexAttr !== null && stopIndexAttr !== '') {
    const stopIndex: number = parseInt(stopIndexAttr);
    
    // Validation: check if stopIndex is valid and workspace has multi-stops
    if (!isNaN(stopIndex) && stopIndex >= 0 && hasMultiStops && workSpace.stops[stopIndex]) {
      // Set stopId for multistop
      stopId = workSpace.stops[stopIndex].id;
      
      // Check if this is unloading space (has priority over cargo space)
      // Check both dataset and direct attribute access
      const isUnloadingSpaceValue = data.isUnloadingSpace !== undefined 
        ? data.isUnloadingSpace 
        : getAttr('isUnloadingSpace');
      const isUnloadingSpaceAttr: boolean = isUnloadingSpaceValue === 'true' || isUnloadingSpaceValue === '';
      
      if (isUnloadingSpaceAttr) {
        // For unloading space we don't set cargoSpaceId (it's a special space)
        cargoSpaceId = null;
      } else {
        const cargoSpaceIndexAttr = data.cargoSpaceIndex !== undefined ? data.cargoSpaceIndex : getAttr('cargoSpaceIndex');
        if (cargoSpaceIndexAttr !== undefined && cargoSpaceIndexAttr !== null && cargoSpaceIndexAttr !== '') {
          // For cargo space we set cargoSpaceId
          const cargoSpaceIndex: number = parseInt(cargoSpaceIndexAttr);
          if (!isNaN(cargoSpaceIndex) && cargoSpaceIndex >= 0 && workSpace.stops[stopIndex].cargoSpaces[cargoSpaceIndex]) {
            cargoSpaceId = workSpace.stops[stopIndex].cargoSpaces[cargoSpaceIndex].id;
          } else {
            // Invalid cargoSpaceIndex - treat as outside space
            cargoSpaceId = null;
          }
        } else {
          // Missing cargoSpaceIndex means outside space for multi-stop
          cargoSpaceId = null;
        }
      }
    } else {
      // If stopIndex is invalid or workspace doesn't have multi-stops, treat as regular cargoSpace
      const cargoSpaceIndexAttr = data.cargoSpaceIndex !== undefined ? data.cargoSpaceIndex : getAttr('cargoSpaceIndex');
      if (cargoSpaceIndexAttr !== undefined && cargoSpaceIndexAttr !== null && cargoSpaceIndexAttr !== '') {
        const cargoSpaceIndex: number = parseInt(cargoSpaceIndexAttr);
        if (!isNaN(cargoSpaceIndex) && cargoSpaceIndex >= 0 && workSpace.cargoSpaces[cargoSpaceIndex]) {
          cargoSpaceId = workSpace.cargoSpaces[cargoSpaceIndex].id;
        } else {
          cargoSpaceId = null;
        }
      } else {
        cargoSpaceId = null;
      }
    }
  } else {
    // For regular workspaces (without multistops)
    // For outside space we set null instead of undefined so it can be serialized in JSON
    const cargoSpaceIndexAttr = data.cargoSpaceIndex !== undefined ? data.cargoSpaceIndex : getAttr('cargoSpaceIndex');
    if (cargoSpaceIndexAttr !== undefined && cargoSpaceIndexAttr !== null && cargoSpaceIndexAttr !== '') {
      const cargoSpaceIndex: number = parseInt(cargoSpaceIndexAttr);
      if (!isNaN(cargoSpaceIndex) && cargoSpaceIndex >= 0 && workSpace.cargoSpaces[cargoSpaceIndex]) {
        cargoSpaceId = workSpace.cargoSpaces[cargoSpaceIndex].id;
      } else {
        cargoSpaceId = null;
      }
    } else {
      cargoSpaceId = null;
    }
  }
  
  // isUnloadingSpace is set only when we are actually in unloading space of multi-stop
  // We check both the attribute and context (whether we have stopIndex)
  // Use helper function to get the value
  const isUnloadingSpaceValue = data.isUnloadingSpace !== undefined 
    ? data.isUnloadingSpace 
    : getAttr('isUnloadingSpace');
  const stopIndexForUnloading = data.stopIndex !== undefined ? data.stopIndex : getAttr('stopIndex');
  
  const isUnloadingSpace: boolean = 
    (isUnloadingSpaceValue === 'true' || isUnloadingSpaceValue === '') &&
    stopIndexForUnloading !== undefined && 
    stopIndexForUnloading !== null && 
    stopIndexForUnloading !== ''
      ? true 
      : undefined;
  
  // Safe parsing of values for getGroupsSorting
  const parsedWorkSpaceIndex: number = parseInt(data.workSpaceIndex || getAttr('workSpaceIndex') || '');
  const cargoSpaceIndexForParsing = data.cargoSpaceIndex !== undefined ? data.cargoSpaceIndex : getAttr('cargoSpaceIndex');
  const parsedCargoSpaceIndex: number = cargoSpaceIndexForParsing !== undefined && cargoSpaceIndexForParsing !== null && cargoSpaceIndexForParsing !== ''
    ? parseInt(cargoSpaceIndexForParsing) 
    : -1;
  const stopIndexForParsing = data.stopIndex !== undefined ? data.stopIndex : getAttr('stopIndex');
  const parsedStopIndex: number = stopIndexForParsing !== undefined && stopIndexForParsing !== null && stopIndexForParsing !== '' 
    ? parseInt(stopIndexForParsing) 
    : -1;
  
  // Build the result object
  const result: any = {
    workSpaceId: workSpaceId,
    groupsOrder: getGroupsSorting(
      parsedWorkSpaceIndex,
      parsedCargoSpaceIndex,
      !isNaN(parsedStopIndex) && parsedStopIndex >= 0 ? parsedStopIndex : -1,
      isUnloadingSpace,
    ),
  };
  
  // Only include cargoSpaceId if it's a valid number (not null/undefined)
  // For outside space, we don't include cargoSpaceId at all (backend requirement)
  if (cargoSpaceId !== null && cargoSpaceId !== undefined) {
    result.cargoSpaceId = cargoSpaceId;
  }
  
  // Include stopId only if it's defined
  if (stopId !== undefined) {
    result.stopId = stopId;
  }
  
  // Include isUnloadingSpace only if it's defined
  if (isUnloadingSpace !== undefined) {
    result.isUnloadingSpace = isUnloadingSpace;
  }
  
  return result;
}

export function getGroupsSorting(
  workSpaceIndex: number,
  cargoSpaceIndex: number,
  stopIndex: number,
  isUnloadingSpace: boolean,
  projectService: ProjectService,
): number[] {
  const state: Project = projectService.getState();

  // Basic validation - check if workspace exists
  if (!state.workSpaces[workSpaceIndex]) {
    return [];
  }

  const workSpace = state.workSpaces[workSpaceIndex];
  const hasMultiStops: boolean = workSpace?.stops?.length > 0;

  // Validation of stopIndex - must be a valid number >= 0 and workspace must have multistops
  const isValidStopIndex: boolean = 
    stopIndex !== undefined && 
    !isNaN(stopIndex) && 
    stopIndex >= 0 && 
    hasMultiStops &&
    workSpace.stops[stopIndex] !== undefined;

  // Validation of cargoSpaceIndex - must be a valid number >= 0
  const isValidCargoSpaceIndex: boolean = 
    cargoSpaceIndex !== undefined && 
    !isNaN(cargoSpaceIndex) && 
    cargoSpaceIndex >= 0;

  // Order of conditions is important - we check from most specific to most general
  
  // 1. CASE: Unloading space for multi-stop (most specific)
  // This is a special space only for multi-stops, where loads are marked for unloading
  if (isUnloadingSpace === true && isValidStopIndex) {
    return workSpace.stops[stopIndex].unloadingSpace?.groups?.map(
      (g: Group) => g.id,
    ) || [];
  }

  // 2. CASE: Cargo space for multi-stop (loads loaded in a specific stop)
  // Requires both valid stopIndex and cargoSpaceIndex
  if (isValidStopIndex && isValidCargoSpaceIndex) {
    const stop = workSpace.stops[stopIndex];
    if (stop.cargoSpaces[cargoSpaceIndex]) {
      return stop.cargoSpaces[cargoSpaceIndex].groups?.map(
        (g: Group) => g.id,
      ) || [];
    }
  }

  // 3. CASE: Outside space for multi-stop (loads outside the cargo area for a specific stop)
  // Requires valid stopIndex, but does NOT require cargoSpaceIndex (because it's outside space)
  if (isValidStopIndex && !isUnloadingSpace) {
    return workSpace.stops[stopIndex].outsideSpace?.groups?.map(
      (g: Group) => g.id,
    ) || [];
  }

  // 4. CASE: Regular cargo space (for workspace without multi-stops)
  // Requires valid cargoSpaceIndex, but does NOT require stopIndex
  if (!hasMultiStops && isValidCargoSpaceIndex) {
    if (workSpace.cargoSpaces[cargoSpaceIndex]) {
      return workSpace.cargoSpaces[cargoSpaceIndex].groups?.map(
        (g: Group) => g.id,
      ) || [];
    }
  }

  // 5. CASE: Regular outside space (for workspace without multi-stops)
  // Last case - when there's no cargoSpaceIndex and no multi-stops
  return workSpace.outsideSpace?.groups?.map(
    (g: Group) => g.id,
  ) || [];
}

function getDropElementAttr(element: HTMLElement | undefined, name: string): string | undefined {
  if (!element) {
    return undefined;
  }
  const data: DOMStringMap = element.dataset || {};
  if (data[name] !== undefined) {
    return data[name];
  }
  const attrNameMap: Record<string, string> = {
    workSpaceIndex: 'work-space-index',
    cargoSpaceIndex: 'cargo-space-index',
    stopIndex: 'stop-index',
    isUnloadingSpace: 'is-unloading-space',
  };
  const attrName = attrNameMap[name] || name.replace(/([A-Z])/g, '-$1').toLowerCase();
  const attrValue = element.getAttribute(`data-${attrName}`);
  return attrValue !== null ? attrValue : undefined;
}

/** Real project groups array for a drop-list element (same reference CDK must mutate). */
export function getProjectGroupsForDropElement(
  project: Project,
  element: HTMLElement | undefined,
): Group[] | undefined {
  if (!project || !element) {
    return undefined;
  }
  const workSpaceIndex = parseInt(getDropElementAttr(element, 'workSpaceIndex') || '', 10);
  const workSpace = project.workSpaces?.[workSpaceIndex];
  if (!workSpace) {
    return undefined;
  }

  const hasMultiStops = workSpace.stops?.length > 0;
  const stopIndexAttr = getDropElementAttr(element, 'stopIndex');
  const cargoSpaceIndexAttr = getDropElementAttr(element, 'cargoSpaceIndex');
  const isUnloadingSpaceValue = getDropElementAttr(element, 'isUnloadingSpace');
  const isUnloadingSpace =
    isUnloadingSpaceValue === 'true' || isUnloadingSpaceValue === '';

  if (stopIndexAttr !== undefined && stopIndexAttr !== null && stopIndexAttr !== '') {
    const stopIndex = parseInt(stopIndexAttr, 10);
    if (!isNaN(stopIndex) && stopIndex >= 0 && hasMultiStops && workSpace.stops[stopIndex]) {
      if (isUnloadingSpace) {
        return workSpace.stops[stopIndex].unloadingSpace?.groups;
      }
      if (cargoSpaceIndexAttr !== undefined && cargoSpaceIndexAttr !== null && cargoSpaceIndexAttr !== '') {
        const cargoSpaceIndex = parseInt(cargoSpaceIndexAttr, 10);
        if (
          !isNaN(cargoSpaceIndex) &&
          cargoSpaceIndex >= 0 &&
          workSpace.stops[stopIndex].cargoSpaces?.[cargoSpaceIndex]
        ) {
          return workSpace.stops[stopIndex].cargoSpaces[cargoSpaceIndex].groups;
        }
      }
      return workSpace.stops[stopIndex].outsideSpace?.groups;
    }
  }

  if (cargoSpaceIndexAttr !== undefined && cargoSpaceIndexAttr !== null && cargoSpaceIndexAttr !== '') {
    const cargoSpaceIndex = parseInt(cargoSpaceIndexAttr, 10);
    if (!isNaN(cargoSpaceIndex) && cargoSpaceIndex >= 0 && workSpace.cargoSpaces?.[cargoSpaceIndex]) {
      return workSpace.cargoSpaces[cargoSpaceIndex].groups;
    }
  }

  return workSpace.outsideSpace?.groups;
}

export type SyncProjectGroupsAfterCdkDropParams = {
  movedGroup: Group;
  cdkFromData: Group[];
  cdkToData: Group[];
  projectFromGroups: Group[];
  projectToGroups: Group[];
  currentIndex: number;
  sameContainer: boolean;
};

/**
 * CDK cargo drop lists may bind a filtered copy of groups. After CDK mutates that copy,
 * sync the real project arrays so getGroupsSorting builds a correct group/drag payload.
 */
export function syncProjectGroupsAfterCdkDrop(params: SyncProjectGroupsAfterCdkDropParams): void {
  const {
    movedGroup,
    cdkFromData,
    cdkToData,
    projectFromGroups,
    projectToGroups,
    currentIndex,
    sameContainer,
  } = params;

  if (!movedGroup || !projectFromGroups || !projectToGroups) {
    return;
  }

  if (sameContainer) {
    if (projectFromGroups === cdkFromData) {
      return;
    }
    syncFilteredReorder(projectFromGroups, cdkFromData);
    return;
  }

  if (projectFromGroups !== cdkFromData) {
    const idx = projectFromGroups.indexOf(movedGroup);
    if (idx >= 0) {
      projectFromGroups.splice(idx, 1);
    }
  }

  if (projectToGroups !== cdkToData && !projectToGroups.includes(movedGroup)) {
    const insertAt = Math.min(Math.max(currentIndex, 0), projectToGroups.length);
    projectToGroups.splice(insertAt, 0, movedGroup);
  }
}

function syncFilteredReorder(projectGroups: Group[], cdkData: Group[]): void {
  const cdkSet = new Set(cdkData);
  const result: Group[] = [];
  let cdkIdx = 0;
  for (const group of projectGroups) {
    if (cdkSet.has(group)) {
      if (cdkIdx < cdkData.length) {
        result.push(cdkData[cdkIdx++]);
      }
    } else {
      result.push(group);
    }
  }
  while (cdkIdx < cdkData.length) {
    result.push(cdkData[cdkIdx++]);
  }
  projectGroups.length = 0;
  projectGroups.push(...result);
}
